import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, ChevronUp } from 'lucide-react';
import ContactForm from '../components/forms/ContactForm';

const faqs = [
  {
    question: 'Чем трансформаторы ТСЛ отличаются от масляных трансформаторов ТМ?',
    answer: 'Трансформаторы ТСЛ (сухие литые) не содержат горючего масла, что исключает риск возгорания и загрязнения окружающей среды. Они компактнее, могут устанавливаться ближе к потребителям, не требуют масляного хозяйства и систем пожаротушения. ТСЛ имеют более длительный срок службы (30+ лет) и меньшие эксплуатационные расходы, хотя начальные инвестиции выше, чем у ТМ.'
  },
  {
    question: 'Каковы сроки производства и доставки трансформаторов?',
    answer: 'Стандартный срок производства трансформаторов ТСЛ и ТСЗ составляет 50-75 дней в зависимости от мощности и комплектации. Доставка осуществляется по всей территории России и стран СНГ в течение 5-15 дней с момента готовности оборудования. Для срочных проектов возможно ускоренное производство.'
  },
  {
    question: 'Какую гарантию предоставляет компания на оборудование?',
    answer: 'Компания «Проминнова» предоставляет стандартную гарантию 36 месяцев на все поставляемое оборудование с даты отгрузки. При заключении договора на сервисное обслуживание возможно продление гарантийного срока. В течение гарантийного периода мы бесплатно устраняем любые заводские дефекты.'
  },
  {
    question: 'Как выбрать подходящий трансформатор для моего проекта?',
    answer: 'Для правильного выбора трансформатора необходимо учитывать несколько ключевых параметров: требуемую мощность, напряжение первичной и вторичной обмоток, тип исполнения (внутреннее/наружное), особенности эксплуатации, климатические условия. Наши технические специалисты помогут подобрать оптимальное решение на основе вашего технического задания.'
  },
  {
    question: 'Какие документы предоставляются при поставке оборудования?',
    answer: 'При поставке оборудования предоставляется полный комплект документации: паспорт изделия, руководство по эксплуатации, протоколы испытаний, сертификаты соответствия ГОСТ и ТР ТС, гарантийный талон. По запросу предоставляются дополнительные документы для проектирования.'
  },
  {
    question: 'Возможна ли установка трансформатора ТСЛ в существующую подстанцию?',
    answer: 'Да, трансформаторы ТСЛ могут быть установлены в существующие подстанции при соответствии габаритных размеров и технических характеристик. Наши специалисты проведут обследование объекта и подготовят техническое решение по замене оборудования.'
  },
  {
    question: 'Какое обслуживание требуется для трансформаторов ТСЛ?',
    answer: 'Трансформаторы ТСЛ требуют минимального обслуживания по сравнению с масляными. Основные работы включают периодический осмотр, очистку от пыли, проверку контактных соединений. Рекомендуется проводить диагностику состояния изоляции раз в год.'
  },
  {
    question: 'Предоставляете ли вы услуги по монтажу и пусконаладке?',
    answer: 'Да, мы предоставляем полный комплекс услуг по монтажу, пусконаладке и вводу в эксплуатацию поставляемого оборудования. Работы выполняются квалифицированными специалистами с соблюдением всех технических требований и норм безопасности.'
  }
];

export default function FaqPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <Helmet>
        <title>Часто задаваемые вопросы о трансформаторах | Проминнова</title>
        <meta name="description" content="Ответы на частые вопросы о трансформаторах ТСЛ и ТСЗ, КТПН, КСО. Узнайте о преимуществах, гарантии, обслуживании и других аспектах оборудования Проминнова." />
      </Helmet>

      <div className="bg-primary-50 py-12">
        <div className="container-custom">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Ответы на вопросы о трансформаторах и оборудовании
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            Собрали ответы на самые популярные вопросы о нашем оборудовании, условиях поставки и сервисном обслуживании
          </p>
        </div>
      </div>

      <div className="container-custom py-16">
        <div className="max-w-3xl mx-auto mb-16">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden transition-all duration-200"
              >
                <button
                  className="w-full flex justify-between items-center p-6 text-left"
                  onClick={() => toggleFaq(index)}
                >
                  <span className="font-medium text-lg">{faq.question}</span>
                  {openIndex === index ? (
                    <ChevronUp className="flex-shrink-0 ml-4 w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="flex-shrink-0 ml-4 w-5 h-5 text-gray-500" />
                  )}
                </button>
                <div 
                  className={`px-6 overflow-hidden transition-all duration-200 ${
                    openIndex === index ? 'max-h-96 pb-6' : 'max-h-0'
                  }`}
                >
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-4">Остались вопросы?</h2>
              <p className="text-gray-600 mb-6">
                Если вы не нашли ответ на свой вопрос, свяжитесь с нами. Наши специалисты проконсультируют вас по любым вопросам, связанным с оборудованием.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mr-3"></div>
                  <span>Бесплатная консультация</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mr-3"></div>
                  <span>Ответ в течение 2 часов</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mr-3"></div>
                  <span>Техническая поддержка</span>
                </div>
              </div>
            </div>
            <div>
              <ContactForm buttonText="Задать вопрос" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}